import { SortingState } from '@tanstack/react-table';
import {
  isNotSelectAll,
  isValidOption,
} from 'components/forms/multiSelectField/MultiSelectField.constants';
import { SortDirection } from 'components/table/Table.types';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import { getPaginationParams } from 'components/table/utils/pagination';
import { getSortParam } from 'components/table/utils/sorting';
import { endOfDay, parseISO, startOfDay } from 'date-fns';
import {
  GetScheduledMessagesResponseTO,
  ScheduledMessage,
  ScheduledMessageApiTO,
} from 'models/automation/ScheduledMessages';
import { ScheduledMessagesTabFiltersSchemaType } from 'pages/automations/scheduledMessages/filters/ScheduledMessagesTabFilters.schema';
import API from 'services/API';

type QueryReturnType = ExpectedQueryData<ScheduledMessage>;

export const SCHEDULED_MESSAGES_BASE_QUERY_KEY = 'scheduled-messages';

export const SCHEDULED_MESSAGES_SORTING = {
  expectedFireTime: 'SORT_BY_EXPECTED_FIRE_TIME',
  state: 'SORT_BY_STATE',
  messageName: 'SORT_BY_MESSAGE_NAME',
  propertyUid: 'SORT_BY_PROPERTY_UID',
  createdAt: 'SORT_BY_CREATED_AT',
};

export const fetchAllScheduledMessages = async (queryParams: {
  _limit?: number;
  _offset?: number | undefined;
  from?: Date;
  messageName?: string;
  propertyUid?: string[];
  sentVia?: string[];
  sort?: string;
  sortDirection?: SortDirection;
  status?: string[];
  to?: Date;
}) => {
  const stringifyArrayParam = (param: keyof typeof queryParams) => {
    const field = queryParams[param];

    return Array.isArray(field) && field.length
      ? { [param]: field.join(',') }
      : {};
  };

  const response = await API.get<GetScheduledMessagesResponseTO>(
    '/api/internal/triggers/scheduled-messages',
    {
      params: {
        ...queryParams,
        ...(queryParams.from ? { from: queryParams.from.toISOString() } : {}),
        ...(queryParams.to ? { to: queryParams.to.toISOString() } : {}),
        ...stringifyArrayParam('propertyUid'),
        ...stringifyArrayParam('sentVia'),
        ...stringifyArrayParam('status'),
      },
    },
  );

  return response.data;
};

const parseScheduledMessage = (
  data: ScheduledMessageApiTO,
): ScheduledMessage => {
  return {
    ...data,
    createdAt: data.createdAt ? parseISO(data.createdAt) : null,
    expectedFireTime: data.expectedFireTime
      ? parseISO(data.expectedFireTime)
      : null,
    updatedAt: data.updatedAt ? parseISO(data.updatedAt) : null,
  };
};

export const scheduledMessagesQuery = ({
  filters,
  pageIndex,
  pageSize,
  searchTerm,
  sorting,
}: {
  filters: Partial<ScheduledMessagesTabFiltersSchemaType>;
  pageIndex: number;
  pageSize: number;
  searchTerm?: string;
  sorting: SortingState;
}) => ({
  queryKey: [
    SCHEDULED_MESSAGES_BASE_QUERY_KEY,
    'list',
    { filters, pageIndex, searchTerm, sorting },
  ],
  queryFn: async (): Promise<QueryReturnType> => {
    const sort = getSortParam(sorting, SCHEDULED_MESSAGES_SORTING);

    const data = await fetchAllScheduledMessages({
      from: filters.from ? startOfDay(filters.from) : undefined,
      propertyUid: filters.propertyUid
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      sentVia: filters.sentVia
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      status: filters.status
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      to: filters.to ? endOfDay(filters.to) : undefined,
      ...(searchTerm ? { messageName: searchTerm } : {}),
      ...getPaginationParams({ pageIndex, pageSize }),
      ...(sort ? { sort: sort.sort, sortDirection: sort.direction } : {}),
    });

    const { scheduledMessages, _metadata } = data;

    return {
      data: scheduledMessages.map(parseScheduledMessage),
      _metadata,
    };
  },
  enabled: true,
});
